/**
 * LoadingState Component
 * Premium loading indicators - Stripe/Linear/Vercel style
 * Subtle, calm, intentional loading states
 */

import React from 'react';
import { Spin, Skeleton, Card, Space } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { spacing } from '../../theme/designTokens';

/**
 * LoadingState Component
 *
 * @param {string} type - Loading type ('spinner' | 'skeleton' | 'table' | 'card' | 'overlay')
 * @param {string} size - Size ('small' | 'default' | 'large')
 * @param {string} tip - Loading text
 * @param {number} rows - Number of skeleton rows (for skeleton type)
 * @param {boolean} active - Skeleton animation (default: true)
 * @param {number} count - Number of skeleton items (for card type)
 * @param {Object} style - Additional styles
 * @param {ReactNode} children - Content to overlay (for overlay type)
 */
function LoadingState({
  type = 'spinner',
  size = 'default',
  tip,
  rows = 5,
  active = true,
  count = 3,
  style = {},
  children,
}) {
  // Spinner sizes - reduced by ~25% for subtlety
  const spinnerSizes = {
    small: 14,
    default: 18,
    large: 32,
  };

  // Spinner (subtle, centered, with soft pulse)
  if (type === 'spinner') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: 48,
          paddingBottom: 48,
          minHeight: 120,
          ...style,
        }}
      >
        <div
          style={{
            animation: 'subtlePulse 2s ease-in-out infinite',
          }}
        >
          <Spin
            size={size}
            tip={tip}
            indicator={
              <LoadingOutlined
                style={{
                  fontSize: spinnerSizes[size] || spinnerSizes.default,
                  color: '#4B3781',
                }}
                spin
              />
            }
          />
        </div>
        <style>
          {`
            @keyframes subtlePulse {
              0%, 100% { opacity: 0.6; }
              50% { opacity: 1; }
            }
          `}
        </style>
      </div>
    );
  }

  // Skeleton (premium styled with consistent rhythm)
  if (type === 'skeleton') {
    return (
      <div style={{ paddingTop: 48, paddingBottom: 48, ...style }}>
        <Skeleton
          active={active}
          paragraph={{ rows }}
          className="premium-skeleton"
        />
        <style>
          {`
            .premium-skeleton .ant-skeleton-content .ant-skeleton-title,
            .premium-skeleton .ant-skeleton-content .ant-skeleton-paragraph > li {
              border-radius: 6px;
              background: linear-gradient(
                90deg,
                #F4F1FA 25%,
                #F7F5FC 50%,
                #F4F1FA 75%
              );
              background-size: 200% 100%;
              animation: shimmer 2s ease-in-out infinite;
            }
            .premium-skeleton .ant-skeleton-content .ant-skeleton-paragraph > li {
              margin-top: 10px;
            }
            @keyframes shimmer {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}
        </style>
      </div>
    );
  }

  // Table skeleton (anchored with border, premium rhythm)
  if (type === 'table') {
    return (
      <div
        style={{
          borderTop: '1px solid #E4E1E8',
          paddingTop: 16,
          paddingBottom: 48,
          ...style,
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="premium-table-skeleton">
              <Skeleton active={active} avatar paragraph={{ rows: 1 }} />
            </div>
          ))}
        </Space>
        <style>
          {`
            .premium-table-skeleton .ant-skeleton-avatar,
            .premium-table-skeleton .ant-skeleton-content .ant-skeleton-title,
            .premium-table-skeleton .ant-skeleton-content .ant-skeleton-paragraph > li {
              border-radius: 6px;
              background: linear-gradient(
                90deg,
                #F4F1FA 25%,
                #F7F5FC 50%,
                #F4F1FA 75%
              );
              background-size: 200% 100%;
              animation: shimmer 2s ease-in-out infinite;
            }
          `}
        </style>
      </div>
    );
  }

  // Card skeleton (multiple cards with consistent spacing)
  if (type === 'card') {
    return (
      <div style={{ paddingTop: 48, paddingBottom: 48, ...style }}>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {Array.from({ length: count }).map((_, i) => (
            <Card key={i} style={{ width: '100%' }} className="premium-card-skeleton">
              <Skeleton active={active} paragraph={{ rows: 3 }} />
            </Card>
          ))}
        </Space>
        <style>
          {`
            .premium-card-skeleton .ant-skeleton-content .ant-skeleton-title,
            .premium-card-skeleton .ant-skeleton-content .ant-skeleton-paragraph > li {
              border-radius: 6px;
              background: linear-gradient(
                90deg,
                #F4F1FA 25%,
                #F7F5FC 50%,
                #F4F1FA 75%
              );
              background-size: 200% 100%;
              animation: shimmer 2s ease-in-out infinite;
            }
            .premium-card-skeleton .ant-skeleton-content .ant-skeleton-paragraph > li {
              margin-top: 10px;
            }
          `}
        </style>
      </div>
    );
  }

  // Overlay (spinner over content)
  if (type === 'overlay') {
    return (
      <Spin spinning={true} size={size} tip={tip} style={style}>
        {children}
      </Spin>
    );
  }

  return null;
}

export default LoadingState;
